import * as React from 'react';
import { useIntl } from 'react-intl';
import { Platform } from 'react-native';

import { Box, Text } from '@fhs-legacy/universal-components';
import { Halal } from 'components/ucl/svgs/Halal';
import { usePathname } from 'hooks/use-pathname';
import { routes } from 'utils/routing';

import { StoreCardActions, StoreCardCaption, StoreCardSeparator } from './components';
import { StoreCardDetails as StoreCardDetailsNative } from './components/store-card-details.native';
import { StoreCardDetails as StoreCardDetailsWeb } from './components/store-card-details.web';
import {
  Body2,
  Container,
  FadeIn,
  HalalContainer,
  PrimaryText,
  StoreCardActionsContainer,
  WebContainer,
} from './store-card.styled';
import { StoreCardViewProps } from './types';

/** Avoid a `store-card-details.tsx` barrel: Metro can resolve it before `.web.tsx` and ship the native `togglebutton` div on web. */
const StoreCardDetails = Platform.OS === 'web' ? StoreCardDetailsWeb : StoreCardDetailsNative;

export const StoreCardView: React.VFC<StoreCardViewProps> = ({
  isFavorite = false,
  isUpdatingFavorites = false,
  onCardClick,
  onDiagnosticsClick,
  onFavoriteClick,
  storeDetails,
  isSelected,
  isSingleCardUI,
  isStoreLocatorFocusTarget,
  storeLocatorFocusRef,
}) => {
  const { formatMessage } = useIntl();
  const pathname = usePathname();

  const isFavoriteSearch = pathname === routes.signIn;
  const storeAvailability = storeDetails.openText ?? storeDetails.closedText;
  const storeCaptionAccessibility = storeDetails.isHalal
    ? formatMessage({ id: 'isHalalTitle' })
    : storeAvailability;

  const cardDetails = (
    <StoreCardDetails
      onPress={onCardClick}
      dd-action-name={`${storeDetails.title} open accordion`}
      title={storeDetails.title}
      address={storeDetails.address}
      addressAccessibilityLabel={storeDetails.addressAccessibilityLabel}
      isStoreLocatorFocusTarget={isStoreLocatorFocusTarget}
      storeLocatorFocusRef={storeLocatorFocusRef}
      availability={storeCaptionAccessibility}
      caption={
        <StoreCardCaption
          prefix={storeDetails.distanceText}
          suffix={
            <>
              {storeDetails.isHalal && (
                <>
                  <HalalContainer>
                    {
                      //TODO: RN - replace by Icon variant="halal" from UCL
                    }
                    <Halal title={formatMessage({ id: 'isHalalTitle' })} />
                  </HalalContainer>
                  <Text> &bull; </Text>
                </>
              )}
              <Box accessible={Platform.OS !== 'web'}>
                {storeDetails.openText ? (
                  <Body2>{storeDetails.openText}</Body2>
                ) : (
                  <PrimaryText>{storeDetails.closedText}</PrimaryText>
                )}
              </Box>
            </>
          }
        />
      }
      body={storeDetails.body}
      errorMessage={storeDetails.errorMessage}
      isSelected={isSelected}
    />
  );

  const cardActions = (
    <StoreCardActionsContainer
      zIndex="1"
      py="$1"
      px="$2"
      alignItems={isSingleCardUI ? 'flex-start' : 'center'}
    >
      <StoreCardActions
        dd-action-name={`${storeDetails.title} ${isFavorite ? 'unfavorite' : 'favorite'}`}
        isFavorite={isFavorite}
        favoriteIcon={isFavorite ? 'favoriteFill' : 'favorite'}
        onDiagnosticsClick={!isFavoriteSearch ? onDiagnosticsClick : undefined}
        onFavoritesClick={!isFavoriteSearch ? onFavoriteClick : onCardClick}
        isUpdatingFavorites={isUpdatingFavorites}
      />
    </StoreCardActionsContainer>
  );

  const separator = !isSingleCardUI ? <StoreCardSeparator /> : null;

  return (
    <FadeIn>
      {Platform.OS === 'web' ? (
        <WebContainer
          accessible={false}
          isSingleCardUI={isSingleCardUI}
          _web={{ cursor: onCardClick ? 'pointer' : 'default' }}
        >
          {cardDetails}
          {separator}
          {cardActions}
        </WebContainer>
      ) : (
        <Container onPress={onCardClick} accessible={false} isSingleCardUI={isSingleCardUI}>
          {cardDetails}
          {separator}
          {cardActions}
        </Container>
      )}
    </FadeIn>
  );
};
